import React, { useEffect, useMemo, useState } from 'react';
import styles from './ServiceKitList.module.scss';
import { Title, SubTopBar, ListTable, ModalHandle, toArray, Modal } from 'Mri.Web.Common';
import { Button, Input, ModalFooter } from 'reactstrap';
import { observer } from 'mobx-react';
import { useStore } from '../hooks/use-store';
import { ServiceKitModal } from './ServiceKitModal';
import { ServiceKit, ServiceKitAssembly } from './ServiceKitStore';
import { autorun, observable, runInAction } from 'mobx';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { ActiveDisplay } from '../Displays/ActiveDisplay';

export const ServiceKitList = observer(() => {
    const [searchText, setSearchText] = useState('');
    const { serviceKitStore } = useStore();
    const [selectedServiceKit, setSelectedServiceKit] = useState({} as ServiceKit);
    const modalHandle = useMemo(() => new ModalHandle(), []);
    const confirmDeleteModalHandle = useMemo(() => new ModalHandle(), []);

    useEffect(() => {
        serviceKitStore.loadAllServiceKits().catch(error => console.error(error));
    }, [serviceKitStore]);

    const createServiceKit = () => {
        setSelectedServiceKit({});
        autorun(() => modalHandle.open());
    };

    const clearSelectedServiceKit = () => {
        setSelectedServiceKit({});
    };

    const editServiceKit = (serviceKit: ServiceKit) => {
        setSelectedServiceKit(serviceKit);
        modalHandle.open();
    };

    const confirmDeleteServiceKit = (serviceKit: ServiceKit) => {
        setSelectedServiceKit(serviceKit);
        confirmDeleteModalHandle.open();
    };

    const deleteServiceKit = () => {
        if (isEmpty(selectedServiceKit) || !selectedServiceKit.serviceKitGuid) return;
        serviceKitStore
            .deleteServiceKit(selectedServiceKit.serviceKitGuid)
            .then(() => {
                confirmDeleteModalHandle.close();
                setSelectedServiceKit({});
            })
            .catch(error => console.error(error));
    };

    const search = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
        serviceKitStore.searchServiceKitsLocally(event.target.value);
    };

    const AssembliesCell = ({ serviceKit, keyValue }: { serviceKit: ServiceKit; keyValue: string | number }) => {
        return (
            <ul key={keyValue} data-key={keyValue}>
                {serviceKit?.assemblies &&
                    serviceKit.assemblies.map((assembly, index) => (
                        <li key={`${keyValue}-${index}`} data-key={`${keyValue}-${index}`}>
                            {assembly.partNumber}
                        </li>
                    ))}
                {!serviceKit?.assemblies && <li>No associated part assemblies</li>}
            </ul>
        );
    };

    const AssociatedDisplaysCell = ({
        serviceKit,
        keyValue,
    }: {
        serviceKit: ServiceKit;
        keyValue: string | number;
    }) => {
        return (
            <ul key={keyValue} data-key={keyValue}>
                {!serviceKit?.associatedDisplays && <li>No associated displays</li>}
                {serviceKit?.associatedDisplays &&
                    serviceKit.associatedDisplays.map((display: ActiveDisplay, index: string | number) => (
                        <li key={`${keyValue}-${index}`}>{display.serialNumber}</li>
                    ))}
            </ul>
        );
    };

    const headCells = [
        { id: 'name', numeric: false, label: 'Name' },
        { id: 'description', numeric: false, label: 'Description' },
        {
            id: 'assemblies',
            numeric: false,
            label: 'Part Assemblies',
            customCell: (serviceKit: ServiceKit, key: string | number) => (
                <AssembliesCell keyValue={key} serviceKit={serviceKit} />
            ),
        },
        {
            id: 'associatedDisplays',
            numeric: false,
            label: 'Associated Displays',
            customCell: (serviceKit: ServiceKit, key: string | number) => (
                <AssociatedDisplaysCell keyValue={key} serviceKit={serviceKit} />
            ),
        },
        {
            id: 'edit',
            numeric: false,
            label: 'Edit',
            customCell: (serviceKit: ServiceKit, key: string | number) => (
                <Button key={key} data-key={key} onClick={() => editServiceKit(serviceKit)} color={'primary'}>
                    Edit
                </Button>
            ),
        },
        {
            id: 'delete',
            numeric: false,
            label: 'Delete',
            customCell: (serviceKit: ServiceKit, key: string | number) => (
                <Button key={key} data-key={key} onClick={() => confirmDeleteServiceKit(serviceKit)} color={'danger'}>
                    Delete
                </Button>
            ),
        },
    ];

    const ConfirmDeleteModalFooter = () => (
        <>
            <div className="confirm-footer">
                <Button color={'secondary'} onClick={confirmDeleteModalHandle.close}>
                    Cancel
                </Button>
                <Button color={'danger'} onClick={deleteServiceKit}>
                    Delete
                </Button>
            </div>
        </>
    );
    const ConfirmDeleteModal = ({
        serviceKit,
        deleteModalHandle,
    }: {
        serviceKit: ServiceKit;
        deleteModalHandle: ModalHandle;
    }) => (
        <Modal
            modalHandle={deleteModalHandle}
            className={classNames('p-0')}
            size={'sm'}
            footer={ConfirmDeleteModalFooter()}
        >
            <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
            </div>
            <div className="modal-body">
                <div>Are you sure you want to delete the {serviceKit.name} service kit?</div>
                {serviceKit.associatedDisplays && serviceKit?.associatedDisplays.length >= 1 && (
                    <div>This will affect {serviceKit.associatedDisplays.length} displays.</div>
                )}
            </div>
        </Modal>
    );

    return (
        <>
            <div className="">
                <Title>Service Kits</Title>
                <SubTopBar>
                    <div className="border" />
                    <Input
                        type={'text'}
                        value={searchText}
                        onChange={event => search(event)}
                        placeholder="Search Service Kits..."
                    />
                    <button className={classNames('btn btn-primary', styles.button)} onClick={createServiceKit}>
                        Create Service Kit
                    </button>
                </SubTopBar>
                <ServiceKitModal
                    serviceKit={selectedServiceKit}
                    modalHandle={modalHandle}
                    onClosed={clearSelectedServiceKit}
                />
                <ConfirmDeleteModal serviceKit={selectedServiceKit} deleteModalHandle={confirmDeleteModalHandle} />
                {!serviceKitStore.serviceKitsLoading && serviceKitStore.serviceKits.size > 0 && (
                    <ListTable rows={toArray(serviceKitStore.filteredServiceKits)} headCells={headCells} />
                )}
            </div>
        </>
    );
});
